import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { SettingsTabs } from 'utility'

const COPY = {
    [SettingsTabs.User]: 'People who can sign in, and when they last did.',
    [SettingsTabs.Company]: 'Companies, time zones, and name formats.',
    [SettingsTabs.Report]: 'Photos, GPS, and how the PDF is laid out.',
    [SettingsTabs.AccountPlan]: 'Plan, seats, and the renewal date.',
    [SettingsTabs.Theme]: 'Logo and the colour on exported reports.',
    [SettingsTabs.TemplateGroup]: 'Put templates together so a team can find them.',
    [SettingsTabs.Announcement]: 'A short note people see after they sign in.',
    [SettingsTabs.SettingInvoice]: 'Number prefix and the rules for an invoice.',
    [SettingsTabs.MenuPermission]: 'Choose which menus each role can open.',
    'custom-multiple-response': 'Answer sets you can reuse on any template.',
    [SettingsTabs.EscalationPolicies]: 'Reminders when a ticket has been waiting.',
    [SettingsTabs.TicketCategories]: 'Names used when a corrective ticket is opened.',
    [SettingsTabs.CorrectiveActionTickets]: 'When a failed item should open a ticket.',
    [SettingsTabs.HomeDashboard]: 'Which widgets sit on the home screen.',
    [SettingsTabs.VoiceInspection]: 'Company switch, languages, and translation review.',
    [SettingsTabs.ImpersonationHistory]: 'A log when an admin signs in as someone else.',
}

const icon = (paths) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {paths}
    </svg>
)

const ICONS = {
    [SettingsTabs.User]: icon(<><circle cx="12" cy="8" r="3" /><path d="M5.2 19c.9-2.8 3-4.2 6.8-4.2s5.9 1.4 6.8 4.2" /></>),
    [SettingsTabs.Company]: icon(<><path d="M4 20.5h16" /><path d="M6.5 20.5V5.5h7v15" /><path d="M13.5 9.5h4v11" /><path d="M9 9h2M9 12.5h2M9 16h2" /></>),
    [SettingsTabs.Report]: icon(<><path d="M7 3.5h7l4 4V20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1z" /><path d="M14 3.5V8h4.5" /><path d="M9 13h6M9 17h4" /></>),
    [SettingsTabs.AccountPlan]: icon(<><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 10h18" /></>),
    [SettingsTabs.Theme]: icon(<><circle cx="12" cy="12" r="8" /><path d="M12 4a8 8 0 0 0 0 16" /></>),
    [SettingsTabs.TemplateGroup]: icon(<path d="M3.5 8V6.8A1.3 1.3 0 0 1 4.8 5.5H9l1.6 1.6h8.1A1.3 1.3 0 0 1 20 8.4V17a1.3 1.3 0 0 1-1.3 1.3H4.8A1.3 1.3 0 0 1 3.5 17V8z" />),
    [SettingsTabs.Announcement]: icon(<path d="M5 10v4l2 .5 1.1 3.6a1 1 0 0 0 1.2.6l1.4-.4a1 1 0 0 0 .6-1.2L10.4 14H15l5.5 2.6V7.4L15 10H7a2 2 0 0 0-2 0z" />),
    [SettingsTabs.SettingInvoice]: icon(<><path d="M7 3.5h10v17l-1.8-1.1L13.5 20.5 11.7 19.4 10 20.5 8.2 19.4 6.5 20.5v-17z" /><path d="M9.5 8.5h5M9.5 12h5" /></>),
    [SettingsTabs.MenuPermission]: icon(<path d="M12 3.2 19 6v5.4c0 4.3-2.9 7.2-7 8.6-4.1-1.4-7-4.3-7-8.6V6l7-2.8z" />),
    'custom-multiple-response': icon(<><path d="M8 7h12M8 12h12M8 17h8" /><path d="M4 7h.01M4 12h.01M4 17h.01" /></>),
    [SettingsTabs.EscalationPolicies]: icon(<><path d="M6 16.5V11a6 6 0 1 1 12 0v5.5l1.2 2H4.8L6 16.5z" /><path d="M10 19.2a2 2 0 0 0 4 0" /></>),
    [SettingsTabs.TicketCategories]: icon(<><path d="M4 12.5 12.2 4.3h6.3v6.3L10.3 19 4 12.5z" /><circle cx="16.2" cy="8" r="1" /></>),
    [SettingsTabs.CorrectiveActionTickets]: icon(<path d="M4 8a2 2 0 0 0 2-2h12a2 2 0 0 0 2 2v2a2 2 0 0 1 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V8z" />),
    [SettingsTabs.HomeDashboard]: icon(<><rect x="3.5" y="4" width="17" height="16" rx="2" /><path d="M3.5 9h17M9 9v11" /></>),
    [SettingsTabs.VoiceInspection]: icon(<><path d="M12 3.5a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0v-5a3 3 0 0 0-3-3z" /><path d="M6.5 11.5a5.5 5.5 0 0 0 11 0M12 17v3.5" /></>),
    [SettingsTabs.ImpersonationHistory]: icon(<><circle cx="12" cy="12" r="8" /><path d="M12 8v4.5l3 2" /></>),
}

const Home = styled.div`
    font-family: Outfit, "Segoe UI", sans-serif;
    color: #1c1917;
    background: #f3f1ec;
    border-radius: 0;
    /* Pull through the content segment's 1em padding so cards use the full width. */
    margin: -1em;
    padding: 16px 14px 28px;
    min-height: calc(100vh - 70px);

    @media (max-width: 720px) {
        padding: 14px 12px 24px;
    }
`

const HeroRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin: 0 0 10px;

    @media (max-width: 720px) {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
        margin-bottom: 12px;
    }
`

const Title = styled.h1`
    margin: 0;
    font-family: Fraunces, Georgia, serif;
    font-weight: 500;
    font-size: 32px;
    line-height: 1.05;
    letter-spacing: -0.03em;

    @media (max-width: 900px) {
        font-size: 28px;
    }

    @media (max-width: 720px) {
        font-size: 24px;
    }
`

const Lead = styled.p`
    margin: 6px 0 0;
    max-width: 460px;
    color: #78716c;
    font-size: 14px;
    line-height: 1.4;

    @media (max-width: 720px) {
        font-size: 13px;
    }
`

const Search = styled.label`
    position: relative;
    display: flex;
    align-items: center;
    max-width: 520px;
    width: 100%;

    svg {
        position: absolute;
        left: 14px;
        width: 18px;
        height: 18px;
        color: #a8a29e;
        pointer-events: none;
    }

    input {
        width: 100%;
        height: 40px;
        border-radius: 12px;
        border: 1px solid #e7e2d8;
        background: #fff;
        padding: 0 14px 0 38px;
        font: 500 14px Outfit, "Segoe UI", sans-serif;
        color: #1c1917;
    }

    input:focus {
        outline: 2px solid #bfdbfe;
        border-color: #93c5fd;
    }

    @media (max-width: 720px) {
        max-width: none;

        input {
            height: 44px;
            font-size: 16px;
        }
    }
`

const Invite = styled.button`
    height: 34px;
    padding: 0 14px;
    border: 0;
    border-radius: 999px;
    background: #1e3a8a;
    color: #fff;
    font: 700 13px Outfit, "Segoe UI", sans-serif;
    cursor: pointer;
    flex: none;

    &:hover {
        background: #172554;
    }

    @media (max-width: 720px) {
        width: 100%;
        height: 44px;
        font-size: 14px;
    }
`

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-top: 22px;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
        gap: 10px;
        margin-top: 14px;
    }
`

const Card = styled.button`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    text-align: left;
    width: 100%;
    min-height: 76px;
    padding: 12px 14px;
    border-radius: 14px;
    border: 1px solid #e7e2d8;
    background: #fff;
    color: #1c1917;
    cursor: pointer;
    font: inherit;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 28px rgba(28, 25, 23, 0.06);
    }

    @media (max-width: 720px) {
        align-items: center;
        min-height: 64px;
        padding: 14px;

        &:hover {
            transform: none;
        }
    }

    b {
        display: block;
        font-size: 14px;
        letter-spacing: -0.02em;
        margin-bottom: 2px;
    }

    small {
        display: block;
        color: #78716c;
        font-size: 12.5px;
        line-height: 1.35;
    }
`

const IconWrap = styled.span`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #e7eeff;
    color: #1d4ed8;
    display: grid;
    place-items: center;
    flex: none;

    svg {
        width: 20px;
        height: 20px;
    }
`

const Empty = styled.p`
    margin: 8px 0 0;
    color: #78716c;
`

export const SettingsHome = ({ items, onOpen, onInvite, showInvite }) => {
    const [query, setQuery] = useState('')
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return items
        return items.filter((item) => {
            const text = `${item.label} ${COPY[item.key] || ''}`.toLowerCase()
            return text.includes(q)
        })
    }, [items, query])

    return (
        <Home>
            <HeroRow>
                <div>
                    <Title>Settings</Title>
                    <Lead>Every setting for this company. Open one at a time.</Lead>
                </div>
                {showInvite && (
                    <Invite type="button" onClick={onInvite}>
                        Invite Team Member
                    </Invite>
                )}
            </HeroRow>
            <Search>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                    <circle cx="11" cy="11" r="6.5" />
                    <path d="M16 16.5 20 20.5" />
                </svg>
                <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search settings"
                    aria-label="Search settings"
                />
            </Search>
            {filtered.length === 0 ? (
                <Empty>No setting with that name.</Empty>
            ) : (
                <Grid>
                    {filtered.map((item) => (
                        <Card key={item.key} type="button" onClick={() => onOpen(item.key)}>
                            <IconWrap>{ICONS[item.key] || ICONS[SettingsTabs.User]}</IconWrap>
                            <span>
                                <b>{item.label}</b>
                                <small>{COPY[item.key] || 'Open this setting.'}</small>
                            </span>
                        </Card>
                    ))}
                </Grid>
            )}
        </Home>
    )
}
